/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useMutation, useQuery } from 'react-query'

import type { SwitchOrganizationResponse } from '@modules/cloud-api/v1/types'
import { getUserMemberships, switchOrganization } from '@modules/organization-api'
import { queryClient } from '@modules/query'
import { fullPageReload } from '@modules/access-management-components/CrossTabAuthReplication/fullPageReload'
import type { ApiErrorCollection, QueryHookOptions } from '@modules/query/types'
import LocalStorageKey from '@modules/utils/localStorageKeys'
import externalNavigate from '@modules/routing/externalNavigate'

const getUserMembershipsQueryKey = 'GET_USER_MEMBERSHIPS'

export const useGetUserMembershipsQuery = (
  {
    userId,
    includeRoleAssignments = false,
  }: { userId: number | undefined; includeRoleAssignments?: boolean },
  options?: QueryHookOptions,
) => {
  const enabled = (options?.enabled ?? true) && !!userId
  const userIdString = userId !== undefined ? String(userId) : undefined

  return useQuery({
    ...options,
    queryKey: [getUserMembershipsQueryKey, userIdString, includeRoleAssignments],
    enabled,
    // userId is guaranteed to be defined because of the enabled condition
    queryFn: () => getUserMemberships({ userId: userIdString!, includeRoleAssignments }),
  })
}
export const invalidateGetUserMembershipsQuery = (userId?: string) =>
  queryClient.invalidateQueries({
    queryKey:
      userId !== undefined ? [getUserMembershipsQueryKey, userId] : [getUserMembershipsQueryKey],
  })

export const useSwitchOrganizationMutation = () =>
  useMutation<
    SwitchOrganizationResponse,
    ApiErrorCollection,
    { organizationId: string; redirectTo?: string }
  >({
    mutationFn: switchOrganization,
    onSuccess: (_data, { organizationId, redirectTo }) => {
      // A full page reload is needed to ensure that the new organization in the current tab is loaded and the user is logged in
      // First, store the new organization id in the local storage to trigger reload in other tabs through the CrossTabAuthReplication component
      // Then, perform the full page reload in the current tab
      localStorage.setItem(LocalStorageKey.switchedActiveOrganization, organizationId)

      if (redirectTo) {
        externalNavigate(redirectTo, { replace: true })
      } else {
        fullPageReload()
      }
    },
    onError: () => {
      // update the memberships list that might have changed and leads to the error
      invalidateGetUserMembershipsQuery()
    },
  })
